import React, { useState, useEffect } from "react";
import { ICellRendererParams } from "ag-grid-community";
import Checkbox from "src/components/Checkbox/Checkbox";

interface CheckInRendererProps extends ICellRendererParams {
  data: {
    scoreId: string;
    checkedIn: boolean;
  };
  onCheckInChange?: (scoreId: string, checked: boolean) => Promise<void> | void;
}

const pendingCheckInByScoreId = new Map<string, { value: boolean; saving: boolean }>();

const CheckInRenderer: React.FC<CheckInRendererProps> = ({ data, onCheckInChange }) => {
  const [, forceRender] = useState(0);
  const scoreId = data?.scoreId;
  const pendingState = scoreId ? pendingCheckInByScoreId.get(scoreId) : undefined;
  const checkedIn = pendingState ? pendingState.value : !!data?.checkedIn;
  const isSaving = !!pendingState?.saving;

  // Sync with data prop ONLY when the actual boolean value changes (e.g. from a
  // websocket-triggered refetch). Do NOT list `data` (the object reference) as a
  // dependency — AG Grid rebuilds row objects on every parent re-render, so a new
  // `data` reference with the same stale `checkedIn: false` would immediately undo
  // any optimistic update made before the server response arrives.
  useEffect(() => {
    if (!scoreId || !data) {
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.checkedIn]);

  if (!data) return null;

  return (
    <div 
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox
        id={`checkin-checkbox-${data.scoreId}`}
        label=""
        value={checkedIn}
        disabled={isSaving}
        onChange={async (e) => {
          e.stopPropagation();
          if (!scoreId) {
            return;
          }

          const newValue = e.target.checked;
          pendingCheckInByScoreId.set(scoreId, { value: newValue, saving: true });
          forceRender((v) => v + 1);

          if (onCheckInChange) {
            try {
              await onCheckInChange(data.scoreId, newValue);
              pendingCheckInByScoreId.set(scoreId, { value: newValue, saving: false });
            } catch {
              pendingCheckInByScoreId.delete(scoreId);
            }
            forceRender((v) => v + 1);
            return;
          }
          pendingCheckInByScoreId.set(scoreId, { value: newValue, saving: false });
          forceRender((v) => v + 1);
        }}
      />
    </div>
  );
};

export { CheckInRenderer };
